import DataList from './DataList';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Auxiliaire, AuxiliaireCategoryEnum } from '../types/auxiliaire';
import { PriceCategory } from '../types/priceCategory';
import { apiClient } from '../contexts/AuthContext';

interface EditAuxiliaireDialogProps {
  open: boolean;
  auxiliaire: Auxiliaire | null;
  onClose: () => void;
  onSave: (id: number, data: Partial<Auxiliaire>) => void;
}

const EditAuxiliaireDialog: React.FC<EditAuxiliaireDialogProps> = ({ open, auxiliaire, onClose, onSave }) => {
  const [name, setName] = useState<string>('');
  const [category, setCategory] = useState<AuxiliaireCategoryEnum>(AuxiliaireCategoryEnum.AIDANT_FAMILIAL);
  const [hourlyRate, setHourlyRate] = useState<string>('');
  const [nightlyRate, setNightlyRate] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (auxiliaire) {
      setName(auxiliaire.name);
      setCategory(auxiliaire.category);
      setHourlyRate(auxiliaire.hourly_rate?.toString() ?? '');
      setNightlyRate(auxiliaire.nightly_rate?.toString() ?? '');
      setError(null);
    }
  }, [auxiliaire]);

  const handleSave = () => {
    if (!auxiliaire) return;
    if (!name.trim()) {
      setError('Le nom ne peut pas être vide.');
      return;
    }

    const hourly = hourlyRate === '' ? null : parseFloat(hourlyRate);
    const nightly = nightlyRate === '' ? null : parseFloat(nightlyRate);

    if (hourlyRate !== '' && (isNaN(hourly!) || hourly! <= 0)) {
        setError('Veuillez entrer un taux horaire valide supérieur à 0 ou laisser vide pour la valeur par défaut.');
        return;
    }

    if (nightlyRate !== '' && (isNaN(nightly!) || nightly! <= 0)) {
        setError('Veuillez entrer un taux de nuit valide supérieur à 0 ou laisser vide pour la valeur par défaut.');
        return;
    }

    onSave(auxiliaire.id, {
        name: name.trim(),
        category: category,
        hourly_rate: hourly,
        nightly_rate: nightly,
    });
  };

  if (!auxiliaire) return null;

  const defaultHourlyRate = auxiliaire.price_category?.default_hourly_rate;
  const defaultNightlyRate = auxiliaire.price_category?.default_nightly_rate;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Modifier l'auxiliaire {auxiliaire.name}</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Laissez les champs de taux vides pour utiliser les tarifs par défaut de la catégorie.
        </Typography>
        <TextField
          label="Nom"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          select
          label="Catégorie"
          value={category}
          onChange={(e) => setCategory(e.target.value as AuxiliaireCategoryEnum)}
          fullWidth
          margin="normal"
          required
        >
          {Object.values(AuxiliaireCategoryEnum).map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Taux horaire personnalisé (€)"
          type="number"
          value={hourlyRate}
          onChange={(e) => setHourlyRate(e.target.value)}
          fullWidth
          margin="normal"
          inputProps={{ min: 0, step: 0.01 }}
          placeholder={`Défaut : ${defaultHourlyRate?.toFixed(2) ?? 'N/A'} €`}
        />
        <TextField
          label="Taux de nuit personnalisé (€)"
          type="number"
          value={nightlyRate}
          onChange={(e) => setNightlyRate(e.target.value)}
          fullWidth
          margin="normal"
          inputProps={{ min: 0, step: 0.01 }}
          placeholder={`Défaut : ${defaultNightlyRate?.toFixed(2) ?? 'N/A'} €`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Enregistrer les modifications
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper to determine if a rate is custom
const isCustomRate = (rate: number | null | undefined): boolean => {
  return rate !== null && rate !== undefined;
};

// Styled TableRow for alternating colors
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover, // Use a subtle hover color for odd rows
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const AuxiliaireManagement: React.FC = () => {
  const [auxiliaires, setAuxiliaires] = useState<Auxiliaire[]>([]);
  const [newAuxiliaireData, setNewAuxiliaireData] = useState<Partial<Auxiliaire>>({
    name: '',
    category: AuxiliaireCategoryEnum.AIDANT_FAMILIAL,
    hourly_rate: null,
    nightly_rate: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingAuxiliaire, setEditingAuxiliaire] = useState<Auxiliaire | null>(null);
  const [priceCategories, setPriceCategories] = useState<PriceCategory[]>([]);

  useEffect(() => {
    fetchAuxiliaires();
    fetchPriceCategories();
  }, []);

  const fetchPriceCategories = async () => {
    try {
      const response = await apiClient.get<PriceCategory[]>('/api/price-categories');
      setPriceCategories(response.data.map((category: any) => ({
        ...category,
        default_hourly_rate: parseFloat(category.default_hourly_rate),
        default_nightly_rate: parseFloat(category.default_nightly_rate),
      })));
    } catch (error) {
      console.error('Error fetching price categories:', error);
    }
  };

  const fetchAuxiliaires = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<Auxiliaire[]>('/api/auxiliaires');
      setAuxiliaires(response.data);
    } catch (error: any) {
      setError(`Erreur lors de la récupération des auxiliaires: ${error.message || 'Unknown error'}`);
      console.error('Erreur lors de la récupération des auxiliaires :', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setNewAuxiliaireData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const value = event.target.value as AuxiliaireCategoryEnum;
    setNewAuxiliaireData(prev => ({ ...prev, category: value }));
  };

  const handleAddAuxiliaire = async () => {
    if (!newAuxiliaireData.name?.trim() || !newAuxiliaireData.category) return;
    setError(null);
    try {
      const response = await apiClient.post<Auxiliaire>('/api/auxiliaires', {
        name: newAuxiliaireData.name,
        category: newAuxiliaireData.category,
        hourly_rate: null,
        nightly_rate: null,
      });
      setAuxiliaires(prev => [...prev, response.data]);
      setNewAuxiliaireData({
        name: '',
        category: AuxiliaireCategoryEnum.AIDANT_FAMILIAL,
        hourly_rate: null,
        nightly_rate: null,
      });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message = error?.message;
      setError(`Erreur lors de l'ajout de l'auxiliaire: ${detail || message || 'Unknown error'}`);
      console.error('Erreur lors de l ajout de l auxiliaire:', error);
    }
  };

  const handleSaveAuxiliaire = async (id: number, data: Partial<Auxiliaire>) => {
    setError(null);
    try {
      const response = await apiClient.put<Auxiliaire>(`/api/auxiliaires/${id}`, data);
      setAuxiliaires(prev => prev.map(aux => aux.id === id ? response.data : aux));
      setEditingAuxiliaire(null);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message = error?.message;
      setError(`Erreur lors de la modification de l'auxiliaire: ${detail || message || 'Unknown error'}`);
      console.error('Erreur lors de la modification de l auxiliaire:', error);
    }
  };

  const handleDeleteAuxiliaire = async (id: number) => {
    setError(null);
    try {
      await apiClient.delete(`/api/auxiliaires/${id}`);
      setAuxiliaires(prev => prev.filter(aux => aux.id !== id));
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message = error?.message;
      setError(`Erreur lors de la suppression de l'auxiliaire: ${detail || message || 'Unknown error'}`);
      console.error('Erreur lors de la suppression de l auxiliaire:', error);
    }
  };

  const getDefaultRatesForCategory = (category: AuxiliaireCategoryEnum) => {
    const priceCategory = priceCategories.find(pc => pc.category === category);
    return {
      hourlyRate: priceCategory?.default_hourly_rate,
      nightlyRate: priceCategory?.default_nightly_rate,
    };
  };

  // Update displayRate to conditionally apply bold style
  const displayRate = (rate: number | null | undefined, defaultRate: number | undefined, category: AuxiliaireCategoryEnum): React.ReactNode => {
    const isDefault = !isCustomRate(rate);
    const rateToDisplay = isDefault ? defaultRate : rate;
    const tooltipTitle = isDefault ? "Tarif par défaut de la catégorie" : "Tarif personnalisé défini";

    return (
      <Tooltip title={tooltipTitle}>
        <Typography variant="body2" component="span" sx={{ fontWeight: isCustomRate(rate) ? 'bold' : 'normal' }}>
          {rateToDisplay?.toFixed(2) ?? 'N/A'}
        </Typography>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h5" gutterBottom>
        Gestion des Auxiliaires
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box component={Paper} sx={{ mb: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>Ajouter un nouvel auxiliaire</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Nom"
              name="name"
              value={newAuxiliaireData.name}
              onChange={handleInputChange}
              size="small"
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Catégorie"
              name="category"
              value={newAuxiliaireData.category}
              onChange={handleSelectChange}
              size="small"
              required
              fullWidth
            >
              {Object.values(AuxiliaireCategoryEnum).map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              onClick={handleAddAuxiliaire}
              disabled={!newAuxiliaireData.name?.trim() || !newAuxiliaireData.category}
              fullWidth
            >
              Ajouter l'auxiliaire
            </Button>
          </Grid>
        </Grid>
      </Box>

      <DataList
        rows={auxiliaires}
        keyOf={(aux) => aux.id}
        empty="Aucun auxiliaire trouvé."
        fields={[
          { label: 'Nom', primary: true, render: (aux) => aux.name },
          { label: 'Catégorie', render: (aux) => aux.category },
          {
            label: 'Taux horaire (€)',
            render: (aux) => (
              <Box component="span" sx={{ fontWeight: isCustomRate(aux.hourly_rate) ? 700 : 400 }}>
                {displayRate(aux.hourly_rate, getDefaultRatesForCategory(aux.category)?.hourlyRate, aux.category)}
              </Box>
            ),
          },
          {
            label: 'Taux de nuit (€)',
            render: (aux) => (
              <Box component="span" sx={{ fontWeight: isCustomRate(aux.nightly_rate) ? 700 : 400 }}>
                {displayRate(aux.nightly_rate, getDefaultRatesForCategory(aux.category)?.nightlyRate, aux.category)}
              </Box>
            ),
          },
        ]}
        actions={(aux) => (
          <>
            <Tooltip title="Modifier l'auxiliaire">
              <IconButton onClick={() => setEditingAuxiliaire(aux)} color="primary" size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer l'auxiliaire">
              <IconButton onClick={() => handleDeleteAuxiliaire(aux.id)} color="error" size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        table={(
<TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Catégorie</TableCell>
              <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }}>Taux Horaire (€)</TableCell>
              <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }}>Taux de Nuit (€)</TableCell>
              <TableCell align="right" sx={{ minWidth: 150, fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auxiliaires.length > 0 ? (
              auxiliaires.map((aux) => (
                <StyledTableRow key={aux.id}>
                  <TableCell component="th" scope="row">{aux.name}</TableCell>
                  <TableCell>{aux.category}</TableCell>
                  <TableCell>
                    <Typography variant="body2" component="span" sx={{ fontWeight: isCustomRate(aux.hourly_rate) ? 'bold' : 'normal' }}>
                      {displayRate(aux.hourly_rate, getDefaultRatesForCategory(aux.category)?.hourlyRate, aux.category)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" component="span" sx={{ fontWeight: isCustomRate(aux.nightly_rate) ? 'bold' : 'normal' }}>
                      {displayRate(aux.nightly_rate, getDefaultRatesForCategory(aux.category)?.nightlyRate, aux.category)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Supprimer l'auxiliaire">
                      <Button onClick={() => handleDeleteAuxiliaire(aux.id)} color="error" sx={{ p: 0.5, minWidth: 'auto' }}>
                        <CloseIcon />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Modifier l'auxiliaire">
                      <Button onClick={() => setEditingAuxiliaire(aux)} color="primary" sx={{ p: 0.5, minWidth: 'auto' }}>
                        <EditIcon />
                      </Button>
                    </Tooltip>
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              !loading && (
                <StyledTableRow>
                  <TableCell colSpan={5} align="center">
                    Aucun auxiliaire trouvé.
                  </TableCell>
                </StyledTableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
        )}
      />

      <EditAuxiliaireDialog
        open={editingAuxiliaire !== null}
        auxiliaire={editingAuxiliaire}
        onClose={() => setEditingAuxiliaire(null)}
        onSave={handleSaveAuxiliaire}
      />
    </Box>
  );
};

export default AuxiliaireManagement; 